import {
  createUnvalidatedValidationSummary,
  type CoasterProject,
  type TrackEndpointPose,
  type TrackPieceParameters,
} from '../schema/index.ts';
import { length, sub } from '../track/vector.ts';
import type { FinishAssessment, FinishGuidance } from './operations.ts';
import {
  auditModularPiece,
  generateCompositeModularPiece,
  modularPieceLabel,
  modularEndPose,
  type ModularTrackShapeId,
  type ModularPieceGeometry,
  type TrackType,
} from './modular-track.ts';
import { assessCandidateProject, projectPlotBounds } from './validation.ts';

interface SearchStep {
  readonly id: ModularTrackShapeId;
  readonly parameters: TrackPieceParameters;
  readonly trackType: TrackType;
  readonly geometry: ModularPieceGeometry;
}

interface SearchState {
  readonly pose: TrackEndpointPose;
  readonly steps: readonly SearchStep[];
  readonly lengthMeters: number;
  readonly score: number;
}

const MAX_PIECES = 12;
const MAX_LENGTH_METERS = 96;
const MAX_STATES = 25_000;
const DESKTOP_BUDGET_MS = 100;

let fallbackId = 0;

function newStableId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `${prefix}:${uuid}`;
  fallbackId += 1;
  return `${prefix}:finish:${fallbackId.toString(36)}`;
}

function poseKey(pose: TrackEndpointPose): string {
  return [
    pose.position.x.toFixed(3),
    pose.position.y.toFixed(3),
    pose.position.z.toFixed(3),
    pose.headingStep,
    pose.pitchDegrees,
    pose.bankDegrees,
  ].join(':');
}

function poseMatches(first: TrackEndpointPose, second: TrackEndpointPose): boolean {
  return (
    length(sub(first.position, second.position)) <= 0.001 &&
    first.headingStep === second.headingStep &&
    first.pitchDegrees === second.pitchDegrees &&
    first.bankDegrees === second.bankDegrees
  );
}

function headingDistance(first: number, second: number): number {
  const delta = Math.abs(first - second) % 8;
  return Math.min(delta, 8 - delta);
}

function heuristic(pose: TrackEndpointPose, target: TrackEndpointPose): number {
  return (
    length(sub(pose.position, target.position)) +
    Math.abs(pose.position.y - target.position.y) * 1.5 +
    headingDistance(pose.headingStep, target.headingStep) * 7 +
    Math.abs(pose.pitchDegrees - target.pitchDegrees) * 0.2 +
    Math.abs(pose.bankDegrees - target.bankDegrees) * 0.12
  );
}

function insidePlot(project: CoasterProject, geometry: ModularPieceGeometry): boolean {
  const bounds = projectPlotBounds(project);
  return geometry.segments.every(
    (segment) =>
      segment.end.position.x >= bounds.minX &&
      segment.end.position.x <= bounds.maxX &&
      segment.end.position.z >= bounds.minZ &&
      segment.end.position.z <= bounds.maxZ &&
      segment.end.position.y >= bounds.minY &&
      segment.end.position.y <= bounds.maxY,
  );
}

function actionsFor(
  pose: TrackEndpointPose,
  target: TrackEndpointPose,
): readonly {
  id: ModularTrackShapeId;
  parameters: TrackPieceParameters;
  trackType: TrackType;
}[] {
  const actions: {
    id: ModularTrackShapeId;
    parameters: TrackPieceParameters;
    trackType: TrackType;
  }[] = [];
  const normal = (
    id: ModularTrackShapeId,
    parameters: TrackPieceParameters = {},
  ): (typeof actions)[number] => ({ id, parameters, trackType: 'normal' });
  if (pose.bankDegrees !== 0) actions.push(normal('bank_level', { targetBankDegrees: 0 }));

  if (pose.pitchDegrees !== 0) {
    actions.push(normal('straight', { length: 'short' }), normal('straight', { length: 'medium' }));
    actions.push(
      normal(pose.pitchDegrees > 0 ? 'slope_down' : 'slope_up', {
        targetPitchDegrees: 0,
      }),
    );
    if (
      (target.position.y > pose.position.y && pose.pitchDegrees > 0) ||
      (target.position.y < pose.position.y && pose.pitchDegrees < 0)
    )
      actions.push(
        normal(pose.pitchDegrees > 0 ? 'slope_up' : 'slope_down', {
          targetPitchDegrees: (pose.pitchDegrees > 0
            ? Math.min(90, pose.pitchDegrees + 30)
            : Math.max(-90, pose.pitchDegrees - 30)) as NonNullable<
            TrackPieceParameters['targetPitchDegrees']
          >,
        }),
      );
    return actions;
  }

  actions.push(
    normal('straight', { length: 'short' }),
    normal('straight', { length: 'medium' }),
    normal('straight', { length: 'long' }),
  );
  for (const radius of ['small', 'medium', 'large'] as const) {
    actions.push(
      normal('turn_left', { turnDegrees: 45, radius }),
      normal('turn_right', { turnDegrees: 45, radius }),
      normal('turn_left', { turnDegrees: 90, radius }),
      normal('turn_right', { turnDegrees: 90, radius }),
    );
  }
  if (target.position.y > pose.position.y + 0.001)
    actions.push(normal('slope_up', { targetPitchDegrees: 30 }));
  if (target.position.y < pose.position.y - 0.001)
    actions.push(normal('slope_down', { targetPitchDegrees: -30 }));
  return actions;
}

function routeStepLabel(step: SearchStep): string {
  if (step.id === 'straight') {
    const length = step.parameters.length ?? 'medium';
    return `${length[0]!.toUpperCase()}${length.slice(1)} Straight`;
  }
  if (step.id === 'turn_left' || step.id === 'turn_right')
    return `${step.id === 'turn_left' ? 'Left' : 'Right'} Turn ${step.parameters.turnDegrees ?? 45}° ${step.parameters.radius ?? 'medium'}`;
  if (step.id === 'slope_up' || step.id === 'slope_down') {
    const target = step.parameters.targetPitchDegrees ?? step.geometry.exitPose.pitchDegrees;
    return `Slope to ${target === 0 ? 'Level' : `${Math.abs(target)}° ${target > 0 ? 'Up' : 'Down'}`}`;
  }
  if (step.id === 'bank_left' || step.id === 'bank_right' || step.id === 'bank_level') {
    const target = step.parameters.targetBankDegrees ?? step.geometry.exitPose.bankDegrees;
    return `Bank to ${target === 0 ? 'Level' : `${Math.abs(target)}° ${target < 0 ? 'Left' : 'Right'}`}`;
  }
  return modularPieceLabel(step.id);
}

class SearchQueue {
  private readonly items: SearchState[] = [];

  get length(): number {
    return this.items.length;
  }

  push(value: SearchState): void {
    this.items.push(value);
    let index = this.items.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.items[parent]!.score <= value.score) break;
      this.items[index] = this.items[parent]!;
      index = parent;
    }
    this.items[index] = value;
  }

  pop(): SearchState | null {
    const first = this.items[0];
    const tail = this.items.pop();
    if (!first || !tail || this.items.length === 0) return first ?? null;
    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      if (left >= this.items.length) break;
      const child =
        right < this.items.length && this.items[right]!.score < this.items[left]!.score
          ? right
          : left;
      if (this.items[child]!.score >= tail.score) break;
      this.items[index] = this.items[child]!;
      index = child;
    }
    this.items[index] = tail;
    return first;
  }
}

function buildClosedProject(
  project: CoasterProject,
  steps: readonly SearchStep[],
  target: TrackEndpointPose,
): { project: CoasterProject; addedSectionIds: readonly string[] } | null {
  const next = structuredClone(project);
  const firstNode = next.track.nodes[0];
  if (!firstNode) return null;
  const addedSectionIds: string[] = [];
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
    const step = steps[stepIndex]!;
    const sectionIds: string[] = [];
    for (let segmentIndex = 0; segmentIndex < step.geometry.segments.length; segmentIndex += 1) {
      const segment = step.geometry.segments[segmentIndex]!;
      const from = next.track.nodes.at(-1);
      if (!from) return null;
      const closesCircuit =
        stepIndex === steps.length - 1 && segmentIndex === step.geometry.segments.length - 1;
      const endNode = closesCircuit
        ? firstNode
        : {
            ...structuredClone(segment.end),
            id: newStableId('node'),
          };
      if (!closesCircuit) next.track.nodes.push(endNode);
      const sectionId = newStableId('section');
      next.track.sections.push({
        id: sectionId,
        fromNodeId: from.id,
        toNodeId: endNode.id,
        behavior: closesCircuit
          ? { kind: 'brake', targetSpeedMps: 2.5, decelerationMps2: 8 }
          : structuredClone(segment.behavior),
        geometry: structuredClone(segment.geometry),
      });
      sectionIds.push(sectionId);
      addedSectionIds.push(sectionId);
    }
    next.editor.pieceGroups.push({
      id: newStableId('piece'),
      definitionId: step.id,
      sectionIds,
      parameters: { ...step.parameters, definitionVersion: 2 },
      definitionVersion: 2,
      entryPose: structuredClone(step.geometry.entryPose),
      exitPose: structuredClone(stepIndex === steps.length - 1 ? target : step.geometry.exitPose),
    });
  }
  next.track.closed = true;
  next.validation = createUnvalidatedValidationSummary();
  next.metadata.updatedAt = new Date().toISOString();
  return { project: next, addedSectionIds };
}

export function assessModularFinishToStation(
  project: CoasterProject,
  guidance: FinishGuidance,
): FinishAssessment {
  if (project.track.geometryKind !== 'modular_piecewise_bezier')
    return {
      ok: false,
      code: 'not_enough_room',
      message: 'This coaster does not use modular track.',
      guidance,
    };
  const start = modularEndPose(project);
  const target = project.editor.pieceGroups[0]?.entryPose ?? null;
  if (!start || !target)
    return {
      ok: false,
      code: 'needs_points',
      message: 'Place a station and build more track first.',
      guidance,
    };

  const queue = new SearchQueue();
  queue.push({ pose: start, steps: [], lengthMeters: 0, score: heuristic(start, target) });
  const bestCost = new Map<string, number>([[poseKey(start), 0]]);
  const startedAt = performance.now();
  let explored = 0;

  while (
    queue.length &&
    explored < MAX_STATES &&
    performance.now() - startedAt < DESKTOP_BUDGET_MS
  ) {
    const state = queue.pop();
    if (!state) break;
    explored += 1;
    if (state.steps.length > 0 && poseMatches(state.pose, target)) {
      const built = buildClosedProject(project, state.steps, target);
      if (!built) break;
      const affectedNodes = built.project.track.nodes
        .filter((node) => !project.track.nodes.some((existing) => existing.id === node.id))
        .map((node) => node.id);
      const preflight = assessCandidateProject(
        project,
        built.project,
        affectedNodes,
        built.addedSectionIds,
      );
      if (preflight.canAccept)
        return {
          ok: true,
          project: built.project,
          mode: 'modular',
          message: `Connection fits with ${state.steps.length} standard ${state.steps.length === 1 ? 'piece' : 'pieces'}.`,
          guidance,
          addedSectionIds: built.addedSectionIds,
          routeSteps: state.steps.map((step) => ({
            shapeId: step.id,
            label: routeStepLabel(step),
            parameters: { ...step.parameters },
            trackType: step.trackType,
          })),
          routeLengthM: state.lengthMeters,
        };
    }
    if (state.steps.length >= MAX_PIECES) continue;
    for (const action of actionsFor(state.pose, target)) {
      const result = generateCompositeModularPiece(
        action.id,
        state.pose,
        action.parameters,
        action.trackType,
      );
      if (!result.ok || !insidePlot(project, result.geometry)) continue;
      const stepLength = auditModularPiece(result.geometry).centerlineLengthMeters;
      const lengthMeters = state.lengthMeters + stepLength;
      if (lengthMeters > MAX_LENGTH_METERS) continue;
      const steps = [
        ...state.steps,
        {
          id: action.id,
          parameters: action.parameters,
          trackType: action.trackType,
          geometry: result.geometry,
        },
      ];
      const key = poseKey(result.geometry.exitPose);
      const cost = lengthMeters + steps.length * 1.5;
      if ((bestCost.get(key) ?? Number.POSITIVE_INFINITY) <= cost) continue;
      bestCost.set(key, cost);
      queue.push({
        pose: result.geometry.exitPose,
        steps,
        lengthMeters,
        score: cost + heuristic(result.geometry.exitPose, target) * 1.15,
      });
    }
  }

  const timedOut = explored >= MAX_STATES || performance.now() - startedAt >= DESKTOP_BUDGET_MS;
  return {
    ok: false,
    code: 'not_enough_room',
    message: timedOut
      ? 'No standard-piece route was found within the search limit. Add a turn or straight, then try again.'
      : 'No standard-piece route fits this approach. Match the station height and heading, then try again.',
    guidance,
  };
}
